import React, { useEffect, useState } from 'react';
import { func, string } from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { connect } from 'react-redux';
import config from '../../config';

import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';

import { types as sdkTypes } from '../../util/sdkLoader';
import { getEmbedElement } from '../../util/videoEmbed';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { publishPortfolioWork, closePortfolioWork } from './PortfolioWorkPage.duck';
import {
  Page,
  ResponsiveImage,
  PortfolioImageCarousel,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  AvatarMedium,
  PrimaryButton,
  SecondaryButton,
  NamedLink,
  Modal,
  ToolLogo,
} from '../../components';
import classNames from 'classnames';
import { IoShareOutline, IoSettingsOutline, IoCalendarClearOutline } from 'react-icons/io5';
import { GoBriefcase } from 'react-icons/go';
import { FaRegBuilding } from 'react-icons/fa';

import { TopbarContainer } from '../../containers';
import { loadData } from './PortfolioWorkPage.duck';
import { skillOrToolLabel } from '../../util/skillAndToolLabels';

import css from './PortfolioWorkPage.css';

const { UUID } = sdkTypes;

function extractFormElementsFromValues(values) {
  // Collect all keys that match text_N, video_N, or addImage_N
  const dynamicFieldRegex = /^(text|video|addImage)_(\d+)$/;
  const elements = [];

  // 1. Add elements from field keys (including addImage_N even if no dynamicImageData)
  Object.keys(values).forEach(key => {
    const match = key.match(dynamicFieldRegex);
    if (match) {
      let type;
      if (match[1] === 'text') type = 'FieldTextEditor';
      else if (match[1] === 'video') type = 'FieldTextInput';
      else if (match[1] === 'addImage') type = 'AddImages';
      const index = parseInt(match[2], 10);
      elements.push({ type, id: key, index });
    }
  });

  // 2. Add AddImages elements for all indexes in dynamicImageData (if not already present)
  if (values.dynamicImageData && Array.isArray(values.dynamicImageData)) {
    const addImageIndexes = new Set(values.dynamicImageData.map(d => d.elementIndex));
    addImageIndexes.forEach(index => {
      if (!elements.find(e => e.type === 'AddImages' && e.index === index)) {
        elements.push({ type: 'AddImages', id: `addImage_${index}`, index });
      }
    });
  }

  // 3. Sort by index to preserve order
  elements.sort((a, b) => a.index - b.index);
  return elements;
}

// Helper to get the final visible order of images
function getOrderedImagesForCarousel({ images, dynamicImageData, formElements }) {
  // Filter out invalid images first
  const validImages = images.filter(img => img && img.id && img.id.uuid);

  // 1. Find hero image(s)
  const dynamicImageIds = dynamicImageData?.map(d => d.imageId) || [];
  let heroImages = validImages.filter(img => !dynamicImageIds.includes(img.id.uuid));
  if (heroImages.length === 0 && validImages.length > 0) {
    heroImages = [validImages[0]];
  }

  // 2. Collect dynamic images in form element order
  let dynamicImagesOrdered = [];
  formElements.forEach(element => {
    if (element.type === 'AddImages') {
      const imgs = validImages.filter(img => {
        const mapping = dynamicImageData.find(d => d.imageId === img.id.uuid);
        return mapping && mapping.elementIndex === element.index;
      });
      dynamicImagesOrdered = dynamicImagesOrdered.concat(imgs);
    }
  });

  // 3. Combine hero and dynamic images
  // Remove duplicates (in case hero is also in dynamic)
  const seen = new Set();
  const ordered = [...heroImages, ...dynamicImagesOrdered].filter(img => {
    if (seen.has(img.id.uuid)) return false;
    seen.add(img.id.uuid);
    return true;
  });
  return ordered;
}

export const PortfolioWorkPageComponent = props => {
  const [formElements, setFormElements] = useState([]);
  const [toastMessage, setToastMessage] = useState(null);
  const [closeWorkModalOpen, setCloseWorkModalOpen] = useState(false);
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);
  const [imageCarouselInitialIndex, setImageCarouselInitialIndex] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    intl,
    history,
    scrollingDisabled,
    params: rawParams,
    getListing,
    getOwnListing,
    skillOptions,
    toolOptions,
    currentUser,
    onPublishPortfolioWork,
    onClosePortfolioWork,
    onManageDisableScrolling,
  } = props;

  const locale = config.locale;
  const workId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.type === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.type === LISTING_PAGE_DRAFT_VARIANT;
  const currentWork =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(workId))
      : ensureListing(getListing(workId));

  const author = currentWork?.author || {};
  const ensuredAuthor = ensureUser(author);
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const freelancerDisplayName = author?.attributes?.profile?.displayName || '';
  const authorNoIndex = author?.attributes?.profile?.noIndexing || false;
  const ensuredAuthorBio = author?.attributes?.profile?.bio || '';

  const ownWork =
    currentUser?.id &&
    ensuredAuthor?.id &&
    ensuredAuthor.id.uuid &&
    currentUser.id.uuid === ensuredAuthor.id.uuid;

  const images = currentWork?.images || [];

  const publicData = currentWork?.attributes?.publicData || {};

  useEffect(() => {
    if (
      publicData &&
      (Object.keys(publicData).some(key => /^(text|video|addImage)_\d+$/.test(key)) ||
        publicData.dynamicImageData?.length > 0)
    ) {
      const elements = extractFormElementsFromValues(publicData);
      setFormElements(elements);
    } else {
      setFormElements([]); // Clear if no dynamic fields
    }
  }, [publicData]);

  // Always fetch latest data when id or variant changes
  useEffect(() => {
    if (props.loadData && props.params) {
      props.loadData(props.params);
    }
    // eslint-disable-next-line
  }, [props.params.id, props.params.variant]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownOpen &&
        !event.target.closest(`.${css.settingsIconContainer}`) &&
        !event.target.closest(`.${css.dropdownMenu}`)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const onImageCarouselClose = () => {
    setImageCarouselOpen(false);
  };

  const handleImageClick = imageId => {
    // Find index in orderedImagesForCarousel
    const idx = orderedImagesForCarousel.findIndex(img => img && img.id && img.id.uuid === imageId);
    setImageCarouselInitialIndex(idx >= 0 ? idx : 0);
    setImageCarouselOpen(true);
  };

  const handleShareClick = () => {
    const shareUrl = `${config.canonicalRootURL}/work/${createSlug(title)}/${
      workId.uuid
    }?source=share`;
    // Copy the share URL to clipboard
    if (typeof window !== 'undefined' || navigator.clipboard) {
      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          setToastMessage(intl.formatMessage({ id: 'PortfolioWorkPage.shareSuccess' }));
          setTimeout(() => setToastMessage(null), 2500);
        })
        .catch(err => {
          console.error('Failed to copy share URL:', err);
          setToastMessage(intl.formatMessage({ id: 'PortfolioWorkPage.shareError' }));
          setTimeout(() => setToastMessage(null), 2500);
        });
    }
  };

  const handlePublishClick = async workId => {
    if (workId && onPublishPortfolioWork) {
      await onPublishPortfolioWork(workId);
      history.push(`/work/${createSlug(title)}/${workId.uuid}/`); // Redirect to preview after publishing
    }
  };

  const handleCloseClick = async workId => {
    if (workId && onClosePortfolioWork) {
      await onClosePortfolioWork(workId);
      history.push('/profile-settings');
    }
  };

  const getFirstTextContent = (elements, publicData) => {
    const textElements = elements.filter(el => el.type === 'FieldTextEditor');
    if (textElements.length > 0) {
      const firstTextElement = textElements[0];
      const htmlContent = publicData[firstTextElement.id] || '';
      // Strip HTML tags to get plain text for meta description
      return htmlContent.replace(/<[^>]*>/g, '').trim();
    }
    return '';
  };

  const experience = publicData?.experience || [];
  const tools = publicData?.tools || [];
  const dynamicImageData = publicData?.dynamicImageData || [];
  const dynamicImageIds = dynamicImageData.map(d => d.imageId); // <-- Add this line
  const title = currentWork?.attributes?.title || '';
  const imageRadius = publicData?.imageRadius || false;
  const relatedWorkCustomerType = publicData?.relatedWorkCustomerType || '';
  const relatedWorkExperienceCustomer = publicData?.relatedWorkExperienceCustomer || '';
  const relatedWorkExperienceAgency = publicData?.relatedWorkExperienceAgency || '';
  const isPrivate = publicData?.privateWork || false;
  const jobContinues = publicData?.jobContinues || false;
  const startMonth = publicData?.startMonth?.label || null;
  const startYear = publicData?.startYear?.label || null;
  const endMonth = publicData?.endMonth?.label || null;
  const endYear = publicData?.endYear?.label || null;

  const orderedImagesForCarousel = getOrderedImagesForCarousel({
    images,
    dynamicImageData,
    formElements,
  });

  const dynamicImages = images.filter(
    image => image && image.id && dynamicImageIds && dynamicImageIds.includes(image.id.uuid)
  );

  const heroImage = images.filter(
    image => image && image.id && dynamicImageIds && !dynamicImageIds.includes(image.id.uuid)
  );
  // If no dynamic images, use the first image as hero
  if (heroImage.length === 0 && images.length > 0 && images[0] && images[0].id) {
    heroImage.push(images[0]);
  }

  const getImageVariants = (image, dynamicImageData) => {
    const currentAspect =
      image && image.id ? dynamicImageData.find(d => d.imageId === image.id.uuid)?.aspect : null;

    // Map specific aspect ratios to their variants
    let aspectRatio;
    if (!currentAspect) {
      aspectRatio = 'landscape';
    } else if (Math.abs(currentAspect - 16 / 9) < 0.01) {
      aspectRatio = 'landscape';
    } else if (Math.abs(currentAspect - 1) < 0.01) {
      aspectRatio = 'square';
    } else if (Math.abs(currentAspect - 4 / 5) < 0.01) {
      aspectRatio = 'portrait45';
    } else if (Math.abs(currentAspect - 3 / 4) < 0.01) {
      aspectRatio = 'portrait34';
    } else if (Math.abs(currentAspect - 9 / 16) < 0.01) {
      aspectRatio = 'portrait';
    } else if (currentAspect > 1) {
      aspectRatio = 'landscape';
    } else if (currentAspect < 1) {
      aspectRatio = 'portrait';
    } else {
      aspectRatio = 'square';
    }

    const aspectVariants = {
      landscape: ['horizontal', 'horizontal-2x'],
      portrait: ['vertical', 'vertical-2x'],
      portrait45: ['portrait45', 'portrait45-2x'],
      portrait34: ['portrait34', 'portrait34-2x'],
      square: ['square', 'square2x'],
    };
    return aspectVariants[aspectRatio];
  };

  const facebookImageUrl =
    heroImage.length > 0 ? heroImage[0].attributes?.variants?.facebook?.url : null;

  const pageTitle = title || 'Portfolio Work';

  const topbar = <TopbarContainer currentPage="PortfolioWorkPage" cleanTopBar={true} />;

  const translationKey = locale === 'fi' ? 'fiTranslation' : 'enTranslation';

  // Combine skills and tools; only tools get a logo. Items without a label yet
  // (lists still loading) are left out instead of showing raw keys.
  const skillsAndTools = [...experience, ...tools.map(t => ({ ...t, isTool: true }))]
    .filter(item => item && item.category)
    .map(item => ({
      ...item,
      label: skillOrToolLabel(item.category, [skillOptions, toolOptions], translationKey),
    }))
    .filter(item => item.label);

  const schemaImages = (images, variantName) =>
    (images || [])
      .map(image => {
        const variants = image.attributes.variants;
        const variant = variants ? variants[variantName] : null;

        // deprecated
        // for backwards combatility only
        const sizes = image.attributes.sizes;
        const size = sizes ? sizes.find(i => i.name === variantName) : null;

        return variant || size;
      })
      .filter(variant => variant != null);

  const facebookImages = schemaImages(heroImage, 'facebook');
  const twitterImages = schemaImages(heroImage, 'twitter');

  // Get description: prefer first text content, fallback to title with author info
  const firstTextContent = getFirstTextContent(formElements, publicData);
  const schemaDescription = firstTextContent
    ? firstTextContent.substring(0, 200) // Limit to 200 chars for better SEO
    : title && freelancerDisplayName
    ? `${title} by ${freelancerDisplayName} on Freedomly`
    : ensuredAuthorBio || '';
  const noIndexing = authorNoIndex || isPrivate;

  const canonicalUrl = `${process.env.REACT_APP_CANONICAL_ROOT_URL}/work/${createSlug(title)}/${
    workId.uuid
  }`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    '@id': canonicalUrl,
    name: title,
    headline: title,
    description: schemaDescription,
    url: canonicalUrl,
    image: facebookImages.map(img => img.url),
    datePublished: currentWork?.attributes?.createdAt,
    dateModified: currentWork?.attributes?.updatedAt,
    inLanguage: locale === 'fi' ? 'fi-FI' : 'en-US',
    ...(ensuredAuthor?.id &&
      ensuredAuthor.id.uuid && {
        author: {
          '@type': 'Person',
          '@id': `${process.env.REACT_APP_CANONICAL_ROOT_URL}/u/${ensuredAuthor.id.uuid}`,
          name: freelancerDisplayName,
          url: `${process.env.REACT_APP_CANONICAL_ROOT_URL}/u/${ensuredAuthor.id.uuid}`,
          ...(ensuredAuthorBio && { description: ensuredAuthorBio }),
        },
      }),
    ...(skillsAndTools.length > 0 && {
      keywords: skillsAndTools.map(({ label }) => label).join(', '),
    }),
    about: {
      '@type': 'Organization',
      name: relatedWorkExperienceCustomer,
    },
    isPartOf: {
      '@type': 'WebSite',
      name: 'Freedomly',
      url: process.env.REACT_APP_CANONICAL_ROOT_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Freedomly',
      url: process.env.REACT_APP_CANONICAL_ROOT_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${process.env.REACT_APP_CANONICAL_ROOT_URL}/static/logos/logo.png`,
      },
    },
  };

  if (!currentWork || !currentWork.id || !currentWork.id.uuid) {
    // Still loading the listing

    const loadingTitle = intl.formatMessage({
      id: 'ListingPage.loadingListingTitle',
    });

    return (
      <Page
        title={loadingTitle}
        description={schemaDescription}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        schema={schema}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        listingCanonicalURL={canonicalUrl}
      >
        <LayoutSingleColumn className={css.pageRoot}>
          <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
          <LayoutWrapperMain>
            <p className={css.loadingText}>
              <FormattedMessage id="ListingPage.loadingListingMessage" />
            </p>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return (
    <Page
      title={pageTitle}
      description={schemaDescription}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      contentType="website"
      schema={schema}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      noIndexing={noIndexing}
    >
      <LayoutSingleColumn className={css.pageRoot}>
        <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
        <LayoutWrapperMain>
          {toastMessage && <div className={css.toast}>{toastMessage}</div>}

          {isDraftVariant && ownWork && (
            <div className={css.draftBanner}>
              <div className={css.draftBannerContent}>
                <h3 className={css.draftBannerTitle}>
                  <FormattedMessage id="PortfolioWorkPage.draftBannerTitle" />
                </h3>
                <p className={css.draftBannerText}>
                  <FormattedMessage id="PortfolioWorkPage.draftBannerText" />
                </p>
              </div>
              <div className={css.draftBannerButtons}>
                <NamedLink
                  name="EditPortfolioWorkPage"
                  params={{
                    id: workId.uuid,
                    type: LISTING_PAGE_PARAM_TYPE_DRAFT,
                    slug: createSlug(title),
                  }}
                  className={css.draftBannerEditButton}
                >
                  <FormattedMessage id="PortfolioWorkPage.editButton" />
                </NamedLink>
                <button
                  className={css.draftBannerPublishButton}
                  onClick={() => handlePublishClick(workId)}
                >
                  <FormattedMessage id="PortfolioWorkPage.publishButton" />
                </button>
              </div>
            </div>
          )}

          {ownWork && (
            <div className={css.profileSettingsBreadcrumbs}>
              <NamedLink name="ProfileSettingsPage" className={css.breadcrumbLink}>
                <FormattedMessage id="PortfolioWorkPage.profileSettingsLink" />
              </NamedLink>
            </div>
          )}

          <div className={css.contentContainer}>
            <div className={css.heroContainer}>
              <div className={css.heroInformationContainer}>
                <div className={css.headerRow}>
                  <div className={css.titleAuthorSection}>
                    <AvatarMedium user={ensuredAuthor} className={css.profileImage} />
                    <div className={css.titleAuthorInfo}>
                      <h1 className={css.title}>{title}</h1>
                      {ensuredAuthor?.id?.uuid ? (
                        <NamedLink
                          name="ProfilePage"
                          params={{ id: ensuredAuthor.id.uuid }}
                          className={css.displayName}
                        >
                          {freelancerDisplayName}
                        </NamedLink>
                      ) : (
                        <span className={css.displayName}>{freelancerDisplayName}</span>
                      )}
                    </div>
                  </div>
                  <div className={css.actionsSection}>
                    {!isDraftVariant && (
                      <button className={css.shareButton} onClick={handleShareClick}>
                        <IoShareOutline className={css.shareButtonIcon} />
                        <span className={css.shareButtonText}>
                          <FormattedMessage id="PortfolioWorkPage.shareButton" />
                        </span>
                      </button>
                    )}
                    {ownWork && (
                      <div className={css.settingsIconContainer}>
                        <IoSettingsOutline
                          className={css.settingsIcon}
                          onClick={() => setDropdownOpen(!dropdownOpen)}
                        />
                        {dropdownOpen && (
                          <div className={css.dropdownMenu}>
                            <NamedLink
                              name="EditPortfolioWorkPage"
                              params={{
                                id: workId.uuid,
                                type: isDraftVariant
                                  ? LISTING_PAGE_PARAM_TYPE_DRAFT
                                  : LISTING_PAGE_PARAM_TYPE_EDIT,
                                slug: createSlug(title),
                              }}
                              className={css.dropdownItem}
                              onClick={() => setDropdownOpen(false)}
                            >
                              <FormattedMessage id="PortfolioWorkPage.editButton" />
                            </NamedLink>
                            {isDraftVariant && (
                              <div
                                className={css.dropdownItem}
                                onClick={() => {
                                  setDropdownOpen(false);
                                  handlePublishClick(workId);
                                }}
                              >
                                <FormattedMessage id="PortfolioWorkPage.publishButton" />
                              </div>
                            )}
                            <div
                              className={css.dropdownItem}
                              onClick={() => {
                                setDropdownOpen(false);
                                setCloseWorkModalOpen(true);
                              }}
                            >
                              <FormattedMessage id="PortfolioWorkPage.closeWorkButton" />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info cards section */}
                <div className={css.infoCardsContainer}>
                  {/* Top row: Agency, Client, Timeline */}
                  {(relatedWorkExperienceCustomer ||
                    relatedWorkExperienceAgency ||
                    (startMonth && startYear)) && (
                    <div className={css.infoCardsTopRow}>
                      {/* Agency Card (if applicable) */}
                      {relatedWorkCustomerType === 'agency' && relatedWorkExperienceAgency && (
                        <div className={css.infoCardCompact}>
                          <div className={css.infoCardIconWrapper}>
                            <FaRegBuilding className={css.infoCardIcon} />
                          </div>

                          <div className={css.infoCardValue}>
                            <FormattedMessage id="PortfolioWorkPage.agencyLabel" />
                            {relatedWorkExperienceAgency}
                          </div>
                        </div>
                      )}

                      {/* Client Card */}
                      {relatedWorkExperienceCustomer && (
                        <div className={css.infoCardCompact}>
                          <div className={css.infoCardIconWrapper}>
                            <GoBriefcase className={css.infoCardIcon} />
                          </div>
                          <div className={css.infoCardValue}>{relatedWorkExperienceCustomer}</div>
                        </div>
                      )}

                      {/* Timeline Card */}
                      {startMonth && startYear && (
                        <div className={css.infoCardCompact}>
                          <div className={css.infoCardIconWrapper}>
                            <IoCalendarClearOutline className={css.infoCardIcon} />
                          </div>
                          <div className={css.infoCardValue}>
                            {startMonth}/{startYear}
                            {jobContinues ? (
                              <span>
                                {' '}
                                <FormattedMessage id="ProfilePage.workExperienceDurationOngoing" />
                              </span>
                            ) : (
                              endMonth &&
                              endYear && (
                                <span>
                                  {' '}
                                  - {endMonth}/{endYear}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bottom row: Skills and Tools */}
                  {skillsAndTools.length > 0 && (
                    <div className={css.skillsRow}>
                      {skillsAndTools.map(({ category, isTool, label }) => (
                        <div
                          key={category}
                          className={isTool ? css.toolContainer : css.skillContainer}
                        >
                          {isTool && (
                            <ToolLogo
                              logoUrl={toolOptions.find(t => t.key === category)?.logoUrl}
                              className={css.toolLogo}
                            />
                          )}
                          {label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {heroImage.length > 0 && heroImage[0] && (
              <div className={css.heroImageContainer}>
                <ResponsiveImage
                  rootClassName={classNames(
                    css.rootForImage,
                    imageRadius ? css.imageWithRadius : ''
                  )}
                  alt={title}
                  image={heroImage[0]}
                  variants={['horizontal', 'horizontal-2x']}
                  onClick={() => handleImageClick(heroImage[0]?.id?.uuid)}
                />
              </div>
            )}

            <div className={css.dynamicElementsContainer}>
              {formElements.map((element, index) => {
                if (element.type === 'FieldTextEditor') {
                  return (
                    <div key={element.id} className={css.textEditorContainer}>
                      <div
                        className={css.textEditorContent}
                        dangerouslySetInnerHTML={{ __html: publicData[element.id] || '' }}
                      />
                    </div>
                  );
                } else if (element.type === 'FieldTextInput') {
                  const url = publicData[element.id];
                  const embedElement = getEmbedElement(url);

                  return (
                    <div key={element.id} className={css.videoContainer}>
                      {embedElement ? (
                        <div
                          className={classNames(
                            css.videoPreview,
                            url.includes('instagram.com/reel/') ? css.instagramContainer : '',
                            imageRadius ? css.imageWithRadius : ''
                          )}
                        >
                          {embedElement}
                        </div>
                      ) : null}
                    </div>
                  );
                } else if (element.type === 'AddImages') {
                  const elementImages = dynamicImages.filter(img => {
                    const mapping = dynamicImageData.find(
                      d => d.imageId === (img.id?.uuid || img.id)
                    );
                    if (mapping) {
                      return mapping.elementIndex === element.index;
                    }
                    // Fallback: show only in the first AddImages element
                    const firstAddImagesIndex = formElements.findIndex(e => e.type === 'AddImages');
                    return index === firstAddImagesIndex;
                  });
                  return (
                    <div key={element.id} className={css.imagesContainer}>
                      {elementImages
                        .filter(image => image && image.id && image.id.uuid)
                        .map(image => {
                          // Determine if image is portrait based on aspect ratio
                          const imageAspect = dynamicImageData.find(
                            d => d.imageId === image.id.uuid
                          )?.aspect;
                          const isPortrait = imageAspect && imageAspect < 1;

                          return (
                            <ResponsiveImage
                              rootClassName={classNames(
                                css.rootForImage,
                                imageRadius ? css.imageWithRadius : '',
                                isPortrait ? css.portraitImage : ''
                              )}
                              key={image.id.uuid}
                              alt="Dynamic image"
                              image={image}
                              variants={getImageVariants(image, dynamicImageData)}
                              onClick={() => handleImageClick(image.id.uuid)}
                            />
                          );
                        })}
                    </div>
                  );
                }
                return null;
              })}
            </div>
            <Modal
              id="closeWorkModal"
              isOpen={closeWorkModalOpen}
              onClose={() => setCloseWorkModalOpen(false)}
              contentLabel={intl.formatMessage({ id: 'PortfolioWorkPage.closeWorkModalLabel' })}
              onManageDisableScrolling={props.onManageDisableScrolling}
            >
              <div className={css.closeWorkModalContent}>
                <h2 className={css.closeWorkModalTitle}>
                  <FormattedMessage id="PortfolioWorkPage.closeWorkModalTitle" />
                </h2>
                <p className={css.closeWorkModalText}>
                  <FormattedMessage id="PortfolioWorkPage.closeWorkModalText" />
                </p>
                <div className={css.closeWorkModalButtons}>
                  <SecondaryButton
                    className={css.closeWorkModalCancelButton}
                    onClick={() => setCloseWorkModalOpen(false)}
                  >
                    <FormattedMessage id="PortfolioWorkPage.closeWorkModalCancelButton" />
                  </SecondaryButton>
                  <PrimaryButton
                    className={css.closeWorkModalConfirmButton}
                    onClick={() => handleCloseClick(workId)}
                  >
                    <FormattedMessage id="PortfolioWorkPage.closeWorkModalConfirmButton" />
                  </PrimaryButton>
                </div>
              </div>
            </Modal>
            <Modal
              id="ListingPage.imageCarousel"
              scrollLayerClassName={css.carouselModalScrollLayer}
              containerClassName={css.carouselModalContainer}
              lightCloseButton
              isOpen={imageCarouselOpen}
              onClose={onImageCarouselClose}
              usePortal
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <PortfolioImageCarousel
                images={orderedImagesForCarousel}
                getImageVariants={getImageVariants}
                dynamicImageData={dynamicImageData}
                initialIndex={imageCarouselInitialIndex}
              />
            </Modal>
          </div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

PortfolioWorkPageComponent.defaultProps = {
  className: null,
  rootClassName: null,
};

PortfolioWorkPageComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onManageDisableScrolling: func,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const { currentUser } = state.user;
  const { skillOptions, toolOptions } = state.SkillsAndTools;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);

    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);

    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    skillOptions,
    toolOptions,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: disabled => dispatch(manageDisableScrolling(disabled)),
  onPublishPortfolioWork: workId => dispatch(publishPortfolioWork(workId)),
  onClosePortfolioWork: workId => dispatch(closePortfolioWork(workId)),
});

const PortfolioWorkPage = compose(
  withRouter,
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(PortfolioWorkPageComponent);

PortfolioWorkPage.loadData = loadData;

export default PortfolioWorkPage;
